import { useState } from 'react';
import { useCryptoSales, useDeleteCryptoSale, useSetCryptoSaleState, useUnpayInstallment, useUsers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { CardSkeleton, ConfirmDialog, ErrorState, StatLine } from '@/components/ui';
import { IconPlus } from '@/components/Icons';
import { CryptoSaleCard } from '@/components/crypto/CryptoSaleCard';
import { EditCryptoSaleModal } from '@/components/crypto/EditCryptoSaleModal';
import { NewCryptoSaleModal } from '@/components/crypto/NewCryptoSaleModal';
import { PayInstallmentModal } from '@/components/crypto/PayInstallmentModal';
import { formatBRL, formatBRLShort, formatNumber } from '@/lib/format';
import { formatCrypto, SALE_STATUS_LABELS } from '@/lib/crypto';
import type { CryptoInstallment, CryptoSale, CryptoSaleStatus } from '@/lib/types';

const STATUSES: CryptoSaleStatus[] = ['ACTIVE', 'DONE', 'INTERRUPTED'];

type Pending =
  | { kind: 'pay'; sale: CryptoSale; installment: CryptoInstallment }
  | { kind: 'unpay'; sale: CryptoSale; installment: CryptoInstallment }
  | { kind: 'edit'; sale: CryptoSale }
  | { kind: 'interrupt'; sale: CryptoSale }
  | { kind: 'delete'; sale: CryptoSale };

/** Soma o que falta receber, por moeda: "7.500,00 USDT + US$ 1.000,00". */
function remainingByCurrency(sales: readonly CryptoSale[]): string {
  const totals = new Map<CryptoSale['currency'], number>();
  for (const sale of sales) {
    if (sale.status !== 'ACTIVE') continue;
    totals.set(sale.currency, (totals.get(sale.currency) ?? 0) + sale.progress.remainingAmount);
  }
  const parts = [...totals.entries()].filter(([, amount]) => amount > 0).map(([currency, amount]) => formatCrypto(amount, currency));
  return parts.length > 0 ? parts.join(' + ') : '—';
}

/**
 * Vendas em cripto, a aba dos comprovantes: o cliente paga com o saldo da
 * corretora, que limita o saque por dia, então a venda vira um carnê. Cada
 * parcela marcada como paga vira um comprovante e entra no vendido com o valor
 * em reais do dia.
 */
export function CryptoSalesPanel() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const [status, setStatus] = useState<CryptoSaleStatus | ''>('ACTIVE');
  const [memberId, setMemberId] = useState('');
  const [creating, setCreating] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const { data: members } = useUsers({}, { enabled: isAdmin });
  const { data, isLoading, error } = useCryptoSales({ status: status || undefined, memberId: memberId || undefined });
  const unpay = useUnpayInstallment();
  const setState = useSetCryptoSaleState();
  const remove = useDeleteCryptoSale();

  if (error) return <ErrorState message={(error as Error).message} />;

  const sales = data?.items ?? [];
  const paidBrl = sales.reduce((sum, sale) => sum + sale.progress.paidBrl, 0);
  const overdue = sales.reduce((sum, sale) => sum + (sale.status === 'ACTIVE' ? sale.progress.overdueCount : 0), 0);
  const close = () => setPending(null);

  async function run(action: () => Promise<unknown>, message: string) {
    try {
      await action();
      toast.success(message);
      close();
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function resume(sale: CryptoSale) {
    try {
      await setState.mutateAsync({ id: sale.id, action: 'resume' });
      toast.success('Carnê retomado: as parcelas em aberto voltaram a valer.');
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  return (
    <>
      <p className="muted" style={{ fontSize: 13, margin: '0 0 14px' }}>
        O cliente paga da corretora, em parcelas. Marque cada parcela quando o saque cair: ela entra no vendido com o valor em reais do dia.
      </p>

      <div className="toolbar">
        <select className="inp" value={status} onChange={(event) => setStatus(event.target.value as CryptoSaleStatus | '')} aria-label="Situação">
          {STATUSES.map((option) => <option key={option} value={option}>{SALE_STATUS_LABELS[option]}</option>)}
          <option value="">Todas</option>
        </select>
        {isAdmin ? (
          <select className="inp" value={memberId} onChange={(event) => setMemberId(event.target.value)} aria-label="Membro">
            <option value="">Todos os membros</option>
            {members?.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        ) : null}
        <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>
          <IconPlus /> Nova venda em cripto
        </button>
      </div>

      {data ? (
        <div className="metric-row" style={{ marginBottom: 28 }}>
          <StatLine label="Vendas" value={formatNumber(sales.length)} sub={status ? SALE_STATUS_LABELS[status].toLowerCase() : 'todas'} />
          <StatLine label="Entrou em reais" value={formatBRLShort(paidBrl)} color="var(--emphasis)" sub="já no vendido" />
          <StatLine label="Falta receber" value={remainingByCurrency(sales)} sub="das vendas pagando" />
          <StatLine
            label="Parcelas atrasadas"
            value={formatNumber(overdue)}
            color={overdue > 0 ? 'var(--warning)' : undefined}
            sub={overdue > 0 ? 'cobrar hoje' : 'nenhuma'}
          />
        </div>
      ) : null}

      {isLoading && !data ? (
        <div style={{ display: 'grid', gap: 16 }}><CardSkeleton height={220} /><CardSkeleton height={220} /></div>
      ) : sales.length === 0 ? (
        <div className="empty">
          <p style={{ margin: '0 0 14px' }}>
            {status === 'ACTIVE' ? 'Nenhuma venda em cripto sendo paga agora.' : 'Nenhuma venda em cripto com esse filtro.'}
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            <IconPlus /> Nova venda em cripto
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 18 }}>
          {sales.map((sale) => (
            <CryptoSaleCard
              key={sale.id}
              sale={sale}
              showMember={isAdmin}
              onPay={(installment) => setPending({ kind: 'pay', sale, installment })}
              onUnpay={(installment) => setPending({ kind: 'unpay', sale, installment })}
              onEdit={() => setPending({ kind: 'edit', sale })}
              onInterrupt={() => setPending({ kind: 'interrupt', sale })}
              onResume={() => void resume(sale)}
              onDelete={() => setPending({ kind: 'delete', sale })}
            />
          ))}
        </div>
      )}

      {creating ? <NewCryptoSaleModal onClose={() => setCreating(false)} /> : null}
      {pending?.kind === 'pay' ? <PayInstallmentModal sale={pending.sale} installment={pending.installment} onClose={close} /> : null}
      {pending?.kind === 'edit' ? <EditCryptoSaleModal sale={pending.sale} onClose={close} /> : null}
      {pending?.kind === 'unpay' ? (
        <ConfirmDialog
          title={`Desmarcar a parcela ${pending.installment.number}?`}
          message={
            <>
              Ela volta a ficar em aberto e o comprovante de{' '}
              <strong className="num">{formatBRL(pending.installment.paidBrl ?? 0)}</strong> sai do vendido.
            </>
          }
          confirmLabel="Desmarcar"
          danger
          busy={unpay.isPending}
          onCancel={close}
          onConfirm={() =>
            void run(() => unpay.mutateAsync({ saleId: pending.sale.id, installmentId: pending.installment.id }), 'Parcela desmarcada.')
          }
        />
      ) : null}
      {pending?.kind === 'interrupt' ? (
        <ConfirmDialog
          title="Interromper o carnê?"
          message="Use quando o cliente parar de pagar. As parcelas pagas continuam no vendido; as em aberto são canceladas. Dá para retomar depois."
          confirmLabel="Interromper"
          danger
          busy={setState.isPending}
          onCancel={close}
          onConfirm={() => void run(() => setState.mutateAsync({ id: pending.sale.id, action: 'interrupt' }), 'Carnê interrompido.')}
        />
      ) : null}
      {pending?.kind === 'delete' ? (
        <ConfirmDialog
          title="Excluir a venda?"
          message={`"${pending.sale.description}" ainda não tem parcela paga. O carnê inteiro sai.`}
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          onCancel={close}
          onConfirm={() => void run(() => remove.mutateAsync(pending.sale.id), 'Venda excluída.')}
        />
      ) : null}
    </>
  );
}
